import { Box } from "@chakra-ui/react";
import { generateForm } from "@rjsf/chakra-ui";
import { FormProps } from "@rjsf/core";
import { GenericObjectType, UiSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";

import { UploadTask } from "@nens/brostar/openapi-client";

import { SubmitAndCancel } from "./features/form-extras";
import { RenderFormButtons } from "./features/form-extras/components/form-submit-buttons";
import { FIELDS } from "./form-fields";
import { TEMPLATES } from "./form-templates";
import { BrostarUiSchema } from "./form-ui-options";
import { WIDGETS } from "./form-widgets";
import { useBrostarForm } from "./hooks/use-brostar-form";

// We need to ignore the form typing by default because passing it
// Because for example generateForm<A> and BrostarFormProps<A>
// then you get an error that it doesn't know what A might be
type IgnoreType = { ["ignore"]: "me" };

const Form = generateForm<IgnoreType>();

type BrostarFormProps<A> = Omit<FormProps<A>, "validator" | "uiSchema"> & {
  // Accept either strict BrostarUiSchema or the more general UiSchema
  // This allows passing unified schemas that work across multiple form types
  uiSchema?: BrostarUiSchema<A> | UiSchema;
  renderButtons?: RenderFormButtons;
  uploadTask: UploadTask | undefined;
  onSubmit: FormProps<A>["onSubmit"];
};

export const FORM_ID = "brostar-form";

export const BrostarForm = <A extends GenericObjectType>({
  uploadTask,
  renderButtons,
  ..._props
}: BrostarFormProps<A>) => {
  const { customValidate: passedCustomValidate, ...props } = _props as BrostarFormProps<IgnoreType>;

  const { formRef, customValidate, transformErrors, isValid, handleChange, onReset } =
    useBrostarForm({
      schema: props.schema,
      uiSchema: props.uiSchema,
      passedCustomValidate,
      formData: props.formData,
      onChange: props.onChange,
    });

  return (
    <Box>
      <Form
        id={FORM_ID}
        ref={formRef}
        templates={TEMPLATES}
        validator={validator}
        widgets={WIDGETS}
        fields={FIELDS}
        uiSchema={{
          ...props.uiSchema,
          isRoot: true,
        }}
        customValidate={customValidate}
        transformErrors={transformErrors}
        omitExtraData
        liveOmit
        {...props}
        onChange={handleChange}
      />
      <SubmitAndCancel
        renderButtons={renderButtons}
        uploadTask={uploadTask}
        isValid={isValid}
        formId={FORM_ID}
        onReset={onReset}
      />
    </Box>
  );
};
